import React from 'react';
import { TrackedLink } from 'components/common/TrackedLink';
import { useWorkspacePath } from 'pages/workspaces/useWorkspace';
import { basePaths } from 'app-constants';
import { LinkHelperProps } from './index';
import { useTabbableClick } from './useTabbableClick';
import { useQueryAndHash } from './useQueryAndHash';
import { CaseFilterValue } from '__generated__/graphql';
import queryString from 'util/qs';

interface CaseLinkProps extends LinkHelperProps {
  judicataId: string;
  citator?: boolean;
}

export type { CaseLinkProps };

export function parseCasePath(path: string) {
  // Parse case paths like /primary-law/cases/{judicataId}[/citator]
  const casePathRegex = new RegExp(`^${basePaths.cases}/([^/]+)(?:/citator)?$`);
  const match = path.match(casePathRegex);
  if (match) {
    return {
      judicataId: match[1],
      citator: path.includes('/citator'),
    };
  }
  return null;
}

export function useCasePath(
  judicataId?: string | null,
  citator?: boolean,
  query?: string | null,
  hash?: string | null,
) {
  const workspacePath = useWorkspacePath();

  const path = React.useMemo(() => {
    const parts = [basePaths.cases, judicataId];
    if (citator) {
      parts.push('citator');
    }

    return workspacePath + parts.filter(Boolean).join('/');
  }, [judicataId, citator, workspacePath]);

  return useQueryAndHash(path, query, hash);
}

export function CaseLink({
  judicataId,
  to,
  query,
  hash,
  citator,
  tabbable,
  onClick,
  ...props
}: CaseLinkProps) {
  const path = useCasePath(judicataId, citator, query, hash);
  const handleClick = useTabbableClick(path.pathname, tabbable, onClick);

  return <TrackedLink to={to ? to : path} onClick={handleClick} {...props} />;
}

function useCaseSearchLinkPath(search?: any) {
  const casesPath = useCasePath();
  return {
    ...casesPath,
    search: search ? `?${queryString.stringify(search)}` : undefined,
  };
}

interface CaseTopicSearchLinkProps extends LinkHelperProps {
  topic: CaseFilterValue;
}

export function CaseTopicSearchLink({
  topic,
  to,
  ...props
}: CaseTopicSearchLinkProps) {
  const path = useCaseSearchLinkPath({ optionFilters: `topic_${topic.slug}` });
  return <TrackedLink to={to ? to : path} {...props} />;
}
